import { IContextualMenuItem, IIconProps, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPDefaultButton, IPSeparator } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import EvidenceTaskAdviceModal from 'components/AuditCriteria/EvidenceTaskAdviceModal';
import EvidenceTaskLinks from 'components/AuditCriteria/EvidenceTaskLinks';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import GenericModal from 'components/Dialogs/GenericModal';
import EntityPicker from 'components/Pickers/EntityPicker';
import SingleTask from 'components/Tasks/SingleTask';
import { aiBackgroundIcon, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import {
  auditCheckStatusContradicted,
  auditCheckStatusNotSatisfied,
  auditCheckStatusPartiallySatisfied,
  auditCheckStatusSatisfied,
  EvidenceTaskBinding,
  EvidenceTaskBindingRow,
  getAuditCheckStatusText,
} from 'models/auditCriteria';
import Entity from 'models/entity';
import Task, { TaskTypes } from 'models/tasks/task';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiBindEvidenceTask,
  apiCreateEvidenceTask,
  apiDiscardEvidenceTask,
  apiUnbindEvidenceTask,
} from 'services/Api/auditCriteriaService';
import { apiGetSingleTask, apiGetTaskLookup } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { toLocaleDateShort } from 'utils/datetime';
import { htmlToText } from 'utils/html';

// The task name a criterion gives the evidence task it creates: its requirement, within the column.
const MaxCreatedTaskNameLength = 200;

interface IEvidenceTaskSectionProps {
  entityType: number;
  entityId: number;
  criterionKey: string;
  // What the criterion asks for: the name of a task created from here.
  criterionRequirement: string;
  bindings: EvidenceTaskBinding[];
  readonly?: boolean;
  // The AI assistant is on: the stored advice is offered.
  aiEnabled: boolean;
  disabled?: boolean;
  windowLevel?: number;
  // The entity's bindings after a bind, unbind, create or discard. The host folds them into what it
  // shows; the rows carry no task state or advice, both reappear on its next load.
  onBindingsChanged: (rows: EvidenceTaskBindingRow[]) => Promise<void> | void;
  // A task was worked on in its modal (saved, completed, cancelled): the host reloads what it shows.
  onTasksChanged: () => Promise<void> | void;
}

/**
 * The evidence task of one one-time criterion and everything a user does with it: the bound task with
 * its state, selecting an existing task, creating a new one (created and bound on the server, then
 * opened with the AI evidence pane beside it), opening it the same way, removing it, and the stored AI
 * advice. Shared by the criterion panel and the evidence wizard's cards, so both surfaces behave the
 * same.
 *
 * A criterion holds ONE task, so the two buttons show only while it has none; removing the task frees
 * the slot. Removing a task the criterion path created is also the moment to decide its fate, because
 * it lives nowhere else: the dialog offers to delete it, and without that it stops being hidden and
 * lives on as an ordinary task of the item. A created task the user cancels untouched is offered for
 * discarding, so an accidental click leaves nothing behind.
 */
const EvidenceTaskSection = (props: IEvidenceTaskSectionProps) => {
  const { t } = useTranslation(['criteria', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const theme = useTheme();

  const [isTaskActionPending, setIsTaskActionPending] = useState<boolean>(false);
  const [showSelectTaskModal, setShowSelectTaskModal] = useState<boolean>(false);
  const [pickerTasks, setPickerTasks] = useState<Entity[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);
  const [busyTaskId, setBusyTaskId] = useState<number | undefined>(undefined);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  // The open task was created by this section a moment ago: cancelling it untouched offers a discard.
  const [selectedTaskIsNew, setSelectedTaskIsNew] = useState<boolean>(false);
  const [discardTaskId, setDiscardTaskId] = useState<number | undefined>(undefined);
  const [adviceTaskId, setAdviceTaskId] = useState<number | undefined>(undefined);
  // The task the remove dialog is about, and whether the user asked to delete it with the binding.
  const [removeBinding, setRemoveBinding] = useState<EvidenceTaskBinding | undefined>(undefined);
  const [removeDeletesTask, setRemoveDeletesTask] = useState<boolean>(false);

  const disabled = props.disabled || isTaskActionPending;
  const hasTask = props.bindings.length > 0;

  const runTaskAction = async (action: (accessToken: string) => Promise<void>) => {
    try {
      setIsTaskActionPending(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await action(accessToken);
    } catch (err) {
      // A refusal the server put in words (a criterion that already has a task, a discard it will not
      // do) arrives as a 400 with the text as its body, which the error banner would swallow.
      if (err instanceof AppError && err.code === '400' && !AppError.isControllerException(err)) {
        appContext.showNotification(err.message, true);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsTaskActionPending(false);
    }
  };

  const openSelectTaskModal = async () => {
    setShowSelectTaskModal(true);
    try {
      setIsLoadingTasks(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const entities = await apiGetTaskLookup(true, TaskTypes.Normal, true, accessToken);
      entities.sort((a, b) => a.entityName.localeCompare(b.entityName));
      setPickerTasks(entities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const isTaskAlreadyBound = (entity: Entity): [boolean, string | undefined] => {
    const bound = props.bindings.some((binding) => binding.taskId === entity.entityId);

    return [bound, bound ? t('criteria:Tasks.AlreadyLinked') : undefined];
  };

  const bindEvidenceTask = async (taskId: number) => {
    setBusyTaskId(taskId);
    await runTaskAction(async (accessToken) => {
      const rows = await apiBindEvidenceTask(props.entityType, props.entityId, taskId, [props.criterionKey], accessToken);
      await props.onBindingsChanged(rows);
      setShowSelectTaskModal(false);
      appContext.showNotification(t('criteria:Tasks.Bound'), false, false, true);
    });
    setBusyTaskId(undefined);
  };

  // A task the criterion path created and no other criterion holds has no life outside this
  // criterion, so removing it is where its fate is decided; every other task is only unlinked.
  const decidesTaskFate = (binding: EvidenceTaskBinding | undefined): boolean =>
    binding !== undefined && binding.hidden && binding.criterionCount <= 1;

  const openRemoveDialog = (taskId: number) => {
    if (isTaskActionPending) return;

    setRemoveDeletesTask(false);
    setRemoveBinding(props.bindings.find((binding) => binding.taskId === taskId));
  };

  const removeEvidenceTask = async () => {
    const binding = removeBinding;
    const deleteTask = decidesTaskFate(binding) && removeDeletesTask;
    setRemoveBinding(undefined);
    setRemoveDeletesTask(false);
    if (!binding) return;

    await runTaskAction(async (accessToken) => {
      const rows = await apiUnbindEvidenceTask(
        props.entityType,
        props.entityId,
        binding.taskId,
        props.criterionKey,
        deleteTask,
        accessToken,
      );
      await props.onBindingsChanged(rows);
      const message = deleteTask
        ? t('criteria:Tasks.Remove.Deleted')
        : decidesTaskFate(binding)
          ? t('criteria:Tasks.Remove.Kept')
          : t('criteria:Tasks.Unlinked');
      appContext.showNotification(message, false, false, true);
    });
    // the task itself changed (deleted, or no longer hidden): the host shows it beyond the bindings
    if (decidesTaskFate(binding)) await props.onTasksChanged();
  };

  const loadTask = async (taskId: number, accessToken: string): Promise<Task | undefined> => {
    const tasks = await apiGetSingleTask(taskId, false, accessToken, appContext.globalDataCache);
    if (tasks.tasks.length === 0) {
      appContext.showNotification(t('dashboard:Errors.ItemHasBeenDeleted'), true);

      return undefined;
    }

    return tasks.tasks[0];
  };

  // The server creates the task (Hidden, bound, assigned to the entity's owner) and returns the
  // bindings; the task new to this criterion is the one that was just made. It opens with the AI pane.
  const createEvidenceTask = async () => {
    await runTaskAction(async (accessToken) => {
      const knownTaskIds = new Set(props.bindings.map((binding) => binding.taskId));
      const title = props.criterionRequirement.trim().slice(0, MaxCreatedTaskNameLength) || t('criteria:Tasks.Label');
      const rows = await apiCreateEvidenceTask(props.entityType, props.entityId, [props.criterionKey], title, accessToken);
      await props.onBindingsChanged(rows);

      const created = rows.find(
        (row) => row.criterionKey.toLowerCase() === props.criterionKey.toLowerCase() && !knownTaskIds.has(row.taskId),
      );
      if (!created) return;

      const task = await loadTask(created.taskId, accessToken);
      if (task) {
        setSelectedTaskIsNew(true);
        setSelectedTask(task);
      }
      appContext.showNotification(t('criteria:Tasks.Created'), false, false, true);
    });
  };

  const openTask = async (taskId: number) => {
    if (isTaskActionPending) return;

    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const task = await loadTask(taskId, accessToken);
      if (task) {
        setSelectedTaskIsNew(false);
        setSelectedTask(task);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  // The stored task, not the modal's copy: the AI pane writes into the stored task, so a cancelled
  // modal may sit on top of a task that holds evidence by now.
  const isStoredTaskEmpty = async (taskId: number, accessToken: string): Promise<boolean> => {
    const task = await loadTask(taskId, accessToken);
    if (!task) return false;

    return (
      !htmlToText(task.description).trim() && task.checkList.items.length === 0 && (task.resourceLinkIds ?? []).length === 0
    );
  };

  // SingleTask reports every save through onSave - the evidence pane's silent save before a message
  // included - and closes through close. So closing is handled in close alone, and onSave only records
  // whether the last report was a cancel, which is what decides the discard offer.
  const lastSaveWasCancel = useRef<boolean>(false);

  const onTaskModalClosed = async () => {
    const task = selectedTask;
    const wasNew = selectedTaskIsNew;
    const wasCancelled = lastSaveWasCancel.current;
    lastSaveWasCancel.current = false;
    setSelectedTask(undefined);
    setSelectedTaskIsNew(false);
    if (!task) return;

    if (wasNew && wasCancelled) {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        if (await isStoredTaskEmpty(task.taskId, accessToken)) {
          setDiscardTaskId(task.taskId);

          return;
        }
      } catch (err) {
        appContext.setError(err);
      }
    }

    await props.onTasksChanged();
  };

  const discardTask = async () => {
    const taskId = discardTaskId;
    setDiscardTaskId(undefined);
    if (taskId === undefined) return;

    await runTaskAction(async (accessToken) => {
      const rows = await apiDiscardEvidenceTask(props.entityType, props.entityId, taskId, accessToken);
      await props.onBindingsChanged(rows);
      appContext.showNotification(t('criteria:Tasks.Discard.Done'), false, false, true);
    });
    await props.onTasksChanged();
  };

  const keepTask = async () => {
    setDiscardTaskId(undefined);
    await props.onTasksChanged();
  };

  //
  // AI advice: the bound tasks that hold stored advice, none while AI is off. One task opens its report
  // straight away; several offer a menu, each entry carrying that task's verdict on this criterion.
  //
  const advisedTasks = props.aiEnabled ? props.bindings.filter((binding) => !!binding.aiAdviceStatus) : [];

  // The verdict colours the AI icon the way the badge and the overview column colour their state.
  const getAdviceColor = (status: string | undefined): string | undefined => {
    switch (status) {
      case auditCheckStatusSatisfied:
        return theme.semanticColors.successText;
      case auditCheckStatusPartiallySatisfied:
        return theme.semanticColors.severeWarningIcon;
      case auditCheckStatusNotSatisfied:
      case auditCheckStatusContradicted:
        return theme.semanticColors.errorText;
      default:
        return undefined;
    }
  };

  const getAdviceIconProps = (status: string | undefined): IIconProps => ({
    ...aiBackgroundIcon,
    styles: { root: { color: getAdviceColor(status) } },
  });

  const getAdviceSummary = (binding: EvidenceTaskBinding): string =>
    [getAuditCheckStatusText(binding.aiAdviceStatus, t), binding.aiAdviceOn ? toLocaleDateShort(binding.aiAdviceOn) : '']
      .filter((part) => !!part)
      .join(' - ');

  const getAdviceMenuItem = (binding: EvidenceTaskBinding): IContextualMenuItem => ({
    key: binding.taskId.toString(),
    text: binding.taskName ? binding.taskName : `#${binding.taskId}`,
    secondaryText: getAdviceSummary(binding),
    iconProps: getAdviceIconProps(binding.aiAdviceStatus),
    onClick: () => setAdviceTaskId(binding.taskId),
  });

  //
  // Main render
  //
  return (
    <>
      <IPStack tokens={globalStackTokensGapSmall}>
        <EvidenceTaskLinks
          tasks={props.bindings.map((binding) => ({
            taskId: binding.taskId,
            taskName: binding.taskName,
            taskClosed: binding.taskClosed,
          }))}
          hideEmpty={false}
          disabled={disabled}
          onOpenTask={(taskId) => void openTask(taskId)}
          onUnlink={props.readonly ? undefined : (taskId) => openRemoveDialog(taskId)}
        />
        {((!props.readonly && !hasTask) || advisedTasks.length > 0) && (
          <IPStack horizontal wrap tokens={globalStackTokensGapSmall}>
            {/* a criterion holds one evidence task: while it has one, removing it is the way to another */}
            {!props.readonly && !hasTask && (
              <>
                <IPDefaultButton text={t('criteria:Tasks.Add')} disabled={disabled} onClick={() => void openSelectTaskModal()} />
                <IPDefaultButton
                  text={t('criteria:Tasks.Create')}
                  title={props.aiEnabled ? t('criteria:Tasks.CreateWithAiInfo') : undefined}
                  iconProps={props.aiEnabled ? aiBackgroundIcon : undefined}
                  disabled={disabled}
                  onClick={() => void createEvidenceTask()}
                />
              </>
            )}
            {/* a read, so it stays available on a read-only surface; the advice itself is asked for
                from the AI actions of the criteria editor or written by the evidence assistant */}
            {advisedTasks.length > 0 && (
              <IPDefaultButton
                text={t('criteria:Tasks.Advice.Show')}
                iconProps={getAdviceIconProps(advisedTasks.length === 1 ? advisedTasks[0].aiAdviceStatus : undefined)}
                title={advisedTasks.length === 1 ? getAdviceSummary(advisedTasks[0]) : undefined}
                onClick={advisedTasks.length === 1 ? () => setAdviceTaskId(advisedTasks[0].taskId) : undefined}
                menuProps={advisedTasks.length > 1 ? { items: advisedTasks.map(getAdviceMenuItem) } : undefined}
              />
            )}
          </IPStack>
        )}
      </IPStack>
      {adviceTaskId !== undefined && (
        <EvidenceTaskAdviceModal
          isOpen
          entityType={props.entityType}
          entityId={props.entityId}
          taskId={adviceTaskId}
          onClose={() => setAdviceTaskId(undefined)}
        />
      )}
      {showSelectTaskModal && (
        <GenericModal
          isOpen={showSelectTaskModal}
          onClose={() => setShowSelectTaskModal(false)}
          isBlocking
          isActionPending={isTaskActionPending}
          title={t('criteria:Tasks.SelectTitle')}
          width={500}
          maxWidth={'min(90vw, 500px)'}
          // explicit height, not minHeight: GenericModal's inner stack is verticalFill, which does
          // not resolve against a min-height-only parent
          height={420}
        >
          <IPSeparator />
          <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 12 } }}>
            <IPStack.Item
              styles={{
                root: {
                  border: `1px solid ${theme.palette.neutralLight}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                },
              }}
            >
              <EntityPicker
                entities={pickerTasks}
                addSelectedEntity={(entity) => void bindEvidenceTask(entity.entityId)}
                isLoading={isLoadingTasks}
                isBusyAdding={busyTaskId}
                disabledEntities={isTaskAlreadyBound}
                isOnPanel={false}
                showHeader={false}
                compact={true}
              />
            </IPStack.Item>
          </IPStack>
        </GenericModal>
      )}
      {selectedTask && (
        <SingleTask
          isOpen={true}
          task={selectedTask}
          windowLevel={props.windowLevel}
          evidenceContext={{ entityType: props.entityType, entityId: props.entityId, criterionKeys: [props.criterionKey] }}
          onUpdate={() => undefined}
          close={() => void onTaskModalClosed()}
          onSave={(_task, _isNewTask, isCancelled) => {
            lastSaveWasCancel.current = isCancelled;
          }}
          onRemove={() => {
            setSelectedTask(undefined);
            setSelectedTaskIsNew(false);
            void props.onTasksChanged();
          }}
          navigateExternal={true}
        />
      )}
      <DialogYesNo
        hidden={discardTaskId === undefined}
        title={t('criteria:Tasks.Discard.Title')}
        subText={t('criteria:Tasks.Discard.SubText')}
        onYes={() => void discardTask()}
        onNo={() => void keepTask()}
      />
      <DialogYesNo
        hidden={removeBinding === undefined}
        title={t('criteria:Tasks.Remove.Title')}
        subText={
          decidesTaskFate(removeBinding)
            ? t('criteria:Tasks.Remove.SubTextCreated', { name: removeBinding?.taskName })
            : t('criteria:Tasks.Remove.SubText')
        }
        optionalJSX={
          decidesTaskFate(removeBinding) ? (
            <IPCheckbox
              label={t('criteria:Tasks.Remove.DeleteTask')}
              checked={removeDeletesTask}
              onChange={(_ev, checked) => setRemoveDeletesTask(checked === true)}
            />
          ) : undefined
        }
        optionalJSXGrow
        onYes={() => void removeEvidenceTask()}
        onNo={() => {
          setRemoveBinding(undefined);
          setRemoveDeletesTask(false);
        }}
      />
    </>
  );
};

export default EvidenceTaskSection;
